import { ProductOptionsColumn, ProductOptionsRow } from '@teko/discovery';
import {
  Body,
  Caption,
  Flex,
  LazyImage,
  Shape,
  styled,
} from '@teko/ds-desktop';
import { t } from 'i18n';
import NextLink from 'next/link';
import React, { useState } from 'react';

interface VariantItemProps {
  variant: ProductOptionsRow;
  onSelect: (variant: ProductOptionsColumn) => void;
  visible?: boolean;
}

export const ProductVariantItem: React.FC<VariantItemProps> = ({
  variant,
  onSelect,
  visible = true,
}: VariantItemProps) => {
  const MAX_VARIANTS = 6;
  const [showMore, setShowMore] = useState(false);

  if (!variant.options?.length) return null;

  const filterOptions = (options: typeof variant.options) => {
    return showMore ? options : options.slice(0, MAX_VARIANTS);
  };

  const handleShowMore = () => {
    setShowMore((prev) => !prev);
  };

  return (
    <Shape marginTop="s8">
      <Body level={1} color="textTitle">
        {variant.title}
      </Body>
      <ContentWrapper>
        {filterOptions(variant.options).map((option, index) => (
          <OptionWrapper
            href={`/${option.url}`}
            key={index}
            onClick={(e) => {
              e.preventDefault();
              onSelect(option);
            }}
            isActive={option.selected}
            isVisible={visible}
            blur={option.quantity === 0}
            className={`att-variant-${option.label}-sku-${option.sku}`}
          >
            <Flex
              direction="row"
              align="center"
              justify={!!option.imageUrl ? 'start' : 'center'}
              gap="s8"
              height="100%"
            >
              {!!option.imageUrl && (
                <StyledImage
                  width="auto"
                  height={32}
                  imageWidth={32}
                  src={option.imageUrl}
                />
              )}
              <Caption
                level={1}
                line={2}
                style={{ textAlign: !!option.imageUrl ? 'left' : 'center' }}
              >
                {option.label}
              </Caption>
            </Flex>
            {option.selected && <Corner />}
          </OptionWrapper>
        ))}
      </ContentWrapper>
      {variant.options.length > MAX_VARIANTS && (
        <ShowMore onClick={handleShowMore}>
          {showMore ? t('Rút gọn') : t('Xem thêm')}
        </ShowMore>
      )}
    </Shape>
  );
};

const ContentWrapper = styled.div`
  gap: 8px;
  margin-top: 2px;
  display: grid;
  grid-template-columns: repeat(3, minmax(80px, 1fr));
`;

interface OptionWrapperProps {
  isActive: boolean;
  isVisible?: boolean;
  blur?: boolean;
}

const OptionWrapper = styled(NextLink)<OptionWrapperProps>(
  ({ isActive, theme, isVisible, blur }) => `
    border: 1px ${
      isActive ? theme.colors.primary500 : theme.colors.divider
    } solid;
    position: relative;
    color: ${isActive ? theme.colors.primary500 : 'black'};
    background-color: ${isVisible || isActive === true ? '#fff' : '#e0e0e0'};
    cursor: ${isVisible || isActive === true ? 'pointer' : 'not-allowed'};
    opacity: ${blur ? '0.5' : '1'};
    min-width: 80px;
    height: 40px;
    border-radius: 4px;
    padding: 4px 8px;
    text-decoration: none;
`,
);

const StyledImage = styled(LazyImage)`
  min-width: 32px;
`;

const Corner = styled.div`
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 18px;
  height: 18px;
  background: #0200bc;
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius.s};
  clip-path: polygon(100% 0, 0 100%, 100% 100%);

  &::after {
    content: '✔';
    position: absolute;
    bottom: -4px;
    right: 2px;
    font-size: 9px;
    color: #fff;
  }
`;

const ShowMore = styled.div`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.link500};
  margin-top: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  width: 80px;
  height: 24px;
  text-align: center;
`;
